// src/pages/Admin.js
// Approval-only admin console for Principal registrations.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import api from "../services/api";
import { API_PATHS, ROUTES } from "../config";
import "../styles/Admin.css";

const getEmail = (row) => row?.email || row?.principal_email || "";

const getDisplayName = (row) =>
  row?.display_name || row?.principal_name || row?.name || getEmail(row) || "Pending principal";

const getSchoolName = (row) =>
  row?.school_name || row?.official_name || row?.school?.official_name || row?.school?.name || "";

const Admin = () => {
  const navigate = useNavigate();
  const mountedRef = useRef(true);

  const [pendingPrincipals, setPendingPrincipals] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadPendingPrincipals = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate(ROUTES.LOGIN, { replace: true });
      return;
    }

    setLoading(true);
    setError("");

    try {
      const { data } = await api.get(API_PATHS.PENDING_PRINCIPALS);
      if (!mountedRef.current) return;

      const rows = Array.isArray(data) ? data : [];
      setPendingPrincipals(rows);
      setSelectedEmail((current) => {
        if (current && rows.some((row) => getEmail(row) === current)) return current;
        return getEmail(rows[0]) || "";
      });
    } catch (err) {
      if (!mountedRef.current) return;
      const detail = err?.response?.data?.detail || "Failed to load pending Principal registrations.";
      setError(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadPendingPrincipals();
  }, [loadPendingPrincipals]);

  const actOnPrincipal = async (email, action) => {
    const cleanEmail = String(email || "").trim().toLowerCase();
    if (!cleanEmail) return;

    const label = action === "approve" ? "approve" : "reject";
    const confirmed = window.confirm(`Are you sure you want to ${label} ${cleanEmail}?`);
    if (!confirmed) return;

    setBusyKey(`${action}:${cleanEmail}`);
    setError("");
    setSuccess("");

    try {
      const endpoint =
        action === "approve"
          ? API_PATHS.APPROVE_PRINCIPAL(cleanEmail)
          : API_PATHS.REJECT_PRINCIPAL(cleanEmail);

      await api.post(endpoint);
      setSuccess(
        action === "approve"
          ? `Approved ${cleanEmail}.`
          : `Rejected ${cleanEmail}.`
      );
      await loadPendingPrincipals();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        `Failed to ${label} ${cleanEmail}.`;
      setError(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      if (mountedRef.current) setBusyKey("");
    }
  };

  const selectedPending = pendingPrincipals.find((row) => getEmail(row) === selectedEmail);

  return (
    <div className="admin-container page">
      <div className="admin-header">
        <div>
          <p className="admin-eyebrow">GhrianGhraffy Admin</p>
          <h1>Principal approvals</h1>
          <p className="admin-subtitle">
            Review new Principal registrations and approve or reject access.
          </p>
        </div>

        <button
          type="button"
          className="btn btn-outline"
          onClick={loadPendingPrincipals}
          disabled={loading || !!busyKey}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {error && (
        <p className="error-message" aria-live="polite">
          {error}
        </p>
      )}

      {success && (
        <p className="success-message" aria-live="polite">
          {success}
        </p>
      )}

      <section className="admin-card">
        <h2>Pending Principal registrations</h2>

        {loading ? (
          <p>Loading pending registrations…</p>
        ) : pendingPrincipals.length === 0 ? (
          <p>No pending Principal registrations.</p>
        ) : (
          <>
            <div className="admin-select-row">
              <label htmlFor="pending-principal-select">Choose a Principal</label>
              <select
                id="pending-principal-select"
                value={selectedEmail}
                onChange={(event) => setSelectedEmail(event.target.value)}
                disabled={!!busyKey}
              >
                {pendingPrincipals.map((row) => {
                  const email = getEmail(row);
                  const schoolName = getSchoolName(row);
                  return (
                    <option key={email} value={email}>
                      {getDisplayName(row)}{schoolName ? ` — ${schoolName}` : ""} ({email})
                    </option>
                  );
                })}
              </select>
            </div>

            {selectedPending && (
              <div className="admin-principal-detail">
                <p>
                  <strong>Name:</strong> {getDisplayName(selectedPending)}
                </p>
                <p>
                  <strong>Email:</strong> {getEmail(selectedPending)}
                </p>
                {getSchoolName(selectedPending) && (
                  <p>
                    <strong>School:</strong> {getSchoolName(selectedPending)}
                  </p>
                )}
                {selectedPending.roll_number && (
                  <p>
                    <strong>Roll number:</strong> {selectedPending.roll_number}
                  </p>
                )}
              </div>
            )}

            <div className="admin-actions-row">
              <button
                type="button"
                className="btn btn-primary"
                disabled={!selectedEmail || !!busyKey}
                onClick={() => actOnPrincipal(selectedEmail, "approve")}
              >
                {busyKey === `approve:${selectedEmail}` ? "Approving…" : "Approve selected"}
              </button>

              <button
                type="button"
                className="btn btn-outline btn-danger"
                disabled={!selectedEmail || !!busyKey}
                onClick={() => actOnPrincipal(selectedEmail, "reject")}
              >
                {busyKey === `reject:${selectedEmail}` ? "Rejecting…" : "Reject selected"}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default Admin;
